import { ToastType } from '@components/toast';
import { QuoteRatingPostRequestSchema, QuoteRatingPostResponse } from '@lio/api-types';
import { Box } from '@mui/material';
import { ErrorInstance } from '@pages/QuoteDetails/types';
import { formVersionQueryKeys } from '@services/formVersion/keys';
import { quoteFormQueryKeys } from '@services/quoteForm/keys';
import { quoteRatingQueryKey } from '@services/quoteRating/keys';
import { quoteQueryKey } from '@services/quotes/keys';
import { useStore } from '@store/GlobalStore';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { addSpaces } from '@utils/addSpaces';
import { useApiEnvStore, useClient } from '@utils/api-client';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { Link } from 'react-router-dom';
import { shallow } from 'zustand/shallow';

export const useCreateQuoteRating = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const [debugMode, debugModeOptions] = useApiEnvStore((state) => [state.debugMode, state.debugModeOptions]);
  const [userAuth] = useStore((state) => [state.userAuth], shallow);

  const canSeeOfferingManagement =
    userAuth.isAdmin || userAuth.isPortalAdmin || userAuth.isJrOps || userAuth.isSrOps || userAuth.isUnderwriter;

  const showErrorIds = canSeeOfferingManagement && debugMode && debugModeOptions.showExtraDetails;

  return useMutation({
    mutationFn: async ({ quoteId, ...requestBody }: QuoteRatingPostRequestSchema & { quoteId: string }) => {
      return client
        .post(`quote/${quoteId}/rating`, {
          json: requestBody,
          timeout: 30000,
        })
        .json<QuoteRatingPostResponse>();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: quoteRatingQueryKey.searchWithParams(variables.quoteId),
      });
      queryClient.invalidateQueries({
        queryKey: quoteQueryKey.diff(variables.quoteId),
      });
      queryClient.invalidateQueries({
        queryKey: quoteFormQueryKeys.get(variables.quoteId),
      });
      queryClient.invalidateQueries({
        queryKey: formVersionQueryKeys.get(variables.quoteId),
      });
    },
    onError: async (error: any, variables) => {
      // if enableRateChangeAnalysis is true in params, then return here
      if (variables.enableRateChangeAnalysis) {
        // intentionally prevent stream read of error response here so we can handle it in the calling function
        return;
      }

      const data = await error?.response?.json();

      if (data?.error?.message === 'Quote Rating is already in progress or pending') {
        enqueueSnackbar('Quote rating is already in process', {
          variant: ToastType.Error,
          preventDuplicate: true,
        });
      } else {
        if (data?.error?.validationDetails?.type === 'BOR_CONFLICT') {
          enqueueSnackbar(userAuth.isInternalUser ? 'Quote Declined for BOR conflict' : 'Quote Declined', {
            persist: true,
            variant: ToastType.Error,
          });
          // Refetch updated quote status
          queryClient.invalidateQueries({ queryKey: quoteQueryKey.get(variables.quoteId) });
          return;
        }
        const validationErrorList: string[] = [];
        if (data?.error?.validationDetails?.errors) {
          const { errors } = data.error.validationDetails;

          const validationErrors = errors
            .filter((error: ErrorInstance) => error.error.type === 'ValidationRuleFailed')
            .map((error: ErrorInstance & { ruleId?: string }) => {
              if (showErrorIds && error.ruleId) {
                return (
                  <React.Fragment key={error.ruleId ?? error.error.message}>
                    {error.error.message}
                    <Box
                      component={Link}
                      to={`/portal-admin/offering-management?validationRuleId=${error.ruleId}`}
                      target='_blank'
                      sx={{
                        ml: 0.5,
                        color: 'white',
                        textDecoration: 'underline',
                      }}
                    >
                      (Rule Id: {error.ruleId})
                    </Box>
                  </React.Fragment>
                );
              }
              return error.error.message;
            });
          const missingFieldErrors = errors
            .filter((error: ErrorInstance) => error.error.type === 'MissingRequired')
            .map((error: ErrorInstance) => `Missing Required Field: ${addSpaces(error.attributeName)}`);
          const notListedErrors = errors
            .filter((error: ErrorInstance) => error.error.type === 'NotListed')
            .map((error: ErrorInstance) => `Quote has invalid fields. Invalid value: ${error.value}`);
          const errorList = validationErrors.concat(missingFieldErrors, notListedErrors);
          validationErrorList.push(...errorList);
        }

        if (validationErrorList.length > 0) {
          enqueueSnackbar('Quote Validation Error(s):', {
            persist: true,
            variant: ToastType.ErrorList,
            errorList: validationErrorList,
          });
        } else {
          enqueueSnackbar('Something went wrong', {
            persist: true,
            variant: ToastType.Error,
          });
        }
      }
    },
  });
};
